/**
 * Amylo-Cloud 솔루션 상세 페이지 — 판독보조 (Amyloid PET)
 *
 * PO 원칙 준수:
 * - 원칙1: 양성/음성 자동 판정 없음. Centiloid 수치 표시만. 임상 해석은 담당의 영역.
 * - 원칙5: Centiloid (CL) 글로벌 표준 단위 명시. SUVR 등 비표준 단위 회피.
 * - 원칙6: "외부 영상을 재분석하지 않습니다". 외부 결과는 수치 기록 + 단순 비교만.
 *           직접 비교는 임상 해석 필요.
 */
import { createFileRoute } from '@tanstack/react-router';
import React from 'react';
import { PublicLayout } from '../../components/public-layout.js';
import { SolutionLayout } from '../../components/solution-layout.js';
import { SectionBlock } from '../../components/section-block.js';

const S: React.CSSProperties = { padding: '100px 0', borderTop: '1px solid var(--line)' };

function Page() {
  return (
    <PublicLayout>
      <SolutionLayout
        stage="img"
        badgeLabel="IMG · 판독보조"
        name="Amylo-Cloud"
        tagline="아밀로이드 PET 영상의 Centiloid(CL) 정량 분석. 글로벌 표준 단위로 아밀로이드 축적 정도를 산출하고, 동일 환자의 시기별 수치를 종단 비교합니다."
        domain="Amyloid PET · Centiloid (CL) 글로벌 표준 단위 · ATN-A"
        crossSell={[
          {
            href: '/solutions/morph',
            name: 'Morph',
            desc: 'ATN의 N — MR/CT Z-score 뇌 위축 정량',
            stage: 'img',
          },
          {
            href: '/solutions/scst',
            name: 'SCST',
            desc: '신의료기술 통과 · 5개 인지 영역 표준화 점수',
            stage: 'dx',
          },
          {
            href: '/solutions/pascal-med',
            name: 'PASCAL-Med',
            desc: '의료기관용 디지털 인지 중재 솔루션',
            stage: 'int',
          },
        ]}
        disclaimerItems={[
          {
            label: 'Amylo-Cloud 사용 목적',
            text: 'Amylo-Cloud는 Amyloid PET 영상의 Centiloid(CL) 정량 분석 판독보조 도구입니다. 분석 결과(CL 수치)의 임상적 해석(양성/음성 판정, 치료 방향 결정 등)은 담당의 종합 판단에 따릅니다.',
          },
          {
            label: '외부 영상 재분석 금지',
            text: 'BeauBrain은 외부 영상을 재분석하지 않습니다. 외부 기관 분석 결과와의 단순 수치 비교 시 분석 장비·트레이서·프로토콜이 다를 수 있으므로 임상적 해석이 필요합니다.',
          },
          {
            label: '책임의 분리',
            text: '진료 행위 및 치료 결과에 대한 최종 책임은 담당의 및 해당 의료기관에 있습니다. BeauBrain은 의료기관에 도구를 공급하는 의료기기·서비스 사업자입니다.',
          },
        ]}
      >

        {/* ── Centiloid 개념 설명 + 핵심 지표 ─────────────────── */}
        <section style={S}>
          <div className="container">
            {/* Centiloid 설명 박스 */}
            <div
              style={{
                background: 'linear-gradient(135deg, rgba(167,139,250,0.1) 0%, rgba(139,92,246,0.06) 100%)',
                border: '1px solid rgba(167,139,250,0.3)',
                borderRadius: 20,
                padding: '32px 36px',
                marginBottom: 48,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                글로벌 표준 단위 Centiloid (CL)
              </div>
              <p style={{ fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.7 }}>
                Centiloid(CL)는 ADNI·AIBL 등 국제 합의를 기반으로 한 아밀로이드 PET 정량 평가의 글로벌 표준 단위입니다.
                SUVR 등 장비·트레이서별 단위와 달리, 장비·트레이서·기관이 달라도 단순 비교가 용이하여
                동일 환자의 시기별 CL 수치 추적과 기관 간 수치 비교에 적합합니다.
              </p>
            </div>

            <SectionBlock label="Key Metrics" title="한눈에 보는 Amylo-Cloud">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginTop: 40 }}>
                {[
                  { num: 'PET', label: '분석 영상' },
                  { num: 'CL', label: 'Centiloid 표준 단위' },
                  { num: '종단', label: '시기별 수치 비교' },
                  { num: '외부', label: '수치 기록 가능' },
                ].map(({ num, label }) => (
                  <div key={label} className="b-item" style={{ textAlign: 'center', padding: '32px 20px' }}>
                    <div className="num" style={{ fontSize: 40, fontWeight: 800, color: '#a78bfa', letterSpacing: '-0.03em', lineHeight: 1, marginBottom: 10 }}>
                      {num}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 600 }}>{label}</div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── 3가지 임상 가치 ───────────────────────────────────── */}
        <section style={{ ...S, background: 'var(--bg-surface)' }}>
          <div className="container">
            <SectionBlock
              label="Why Amylo-Cloud"
              title="의료기관에서 영상 정량 분석을 가능하게 합니다"
            >
              <div className="apple-bento" style={{ marginTop: 56 }}>
                <div className="b-item">
                  <span className="b-eyebrow">VALUE 01</span>
                  <div className="b-title">영상 장비 없어도 사용</div>
                  <p className="b-desc">
                    PET 장비가 없는 의원도 사용 가능합니다.
                    환자가 외부 영상의학과에서 받은 PET 영상을 업로드하여 Centiloid 분석을 진행할 수 있습니다.
                  </p>
                </div>
                <div className="b-item">
                  <span className="b-eyebrow">VALUE 02</span>
                  <div className="b-title">글로벌 표준 CL로 객관화</div>
                  <p className="b-desc">
                    육안 판독의 주관성을 보완합니다. 아밀로이드 축적 정도를 Centiloid(CL) 수치로 산출하여
                    장비·트레이서·기관 간 비교를 용이하게 하고, 의사의 판단을 객관적으로 지원합니다.
                    <strong> 양성/음성 등 임상 판정은 담당의 종합 판단에 따릅니다.</strong>
                  </p>
                </div>
                <div className="b-item">
                  <span className="b-eyebrow">VALUE 03</span>
                  <div className="b-title">종단 수치 추적 + ATN 연결</div>
                  <p className="b-desc">
                    동일 환자의 시기별 CL 수치를 차트에서 시계열로 비교할 수 있습니다.
                    외부 의료기관 분석 결과 수치도 차트에 함께 기록하여 단순 비교 가능합니다.
                    SCST·Morph와 통합되어 ATN 풀스택 평가를 지원합니다.
                  </p>
                </div>
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── 핵심 기능 + 외부 영상 정책 ──────────────────────── */}
        <section style={S}>
          <div className="container">
            <SectionBlock label="Features" title="핵심 기능">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginTop: 56 }}>
                {[
                  {
                    title: 'Centiloid (CL) 자동 정량',
                    desc: '아밀로이드 PET 영상을 자동으로 정량화하여 글로벌 표준 단위 CL 값을 산출합니다. 장비·트레이서가 달라도 기관 간·시기 간 단순 비교가 용이합니다.',
                  },
                  {
                    title: '외부 영상 업로드 분석',
                    desc: '영상 장비가 없는 의료기관도 외부 영상의학과 PET 영상(DICOM)을 업로드하여 분석할 수 있습니다.',
                  },
                  {
                    title: '정량 분석 리포트',
                    desc: '영역별 아밀로이드 축적 정도 시각화 + CL 정량 분석 리포트 자동 생성. 양성/음성 판정 등 임상 해석은 담당의 판단 영역입니다.',
                  },
                  {
                    title: 'DICOM 표준 지원',
                    desc: '의료 영상 표준 DICOM 포맷을 지원합니다. 지원 트레이서 목록은 도입 문의 시 안내드립니다.',
                  },
                  {
                    title: '종단 수치 비교 기록',
                    desc: '동일 환자의 시기별 CL 수치를 차트에서 시계열로 확인할 수 있습니다. SCST·Morph 결과와 통합 조회합니다.',
                  },
                  {
                    title: '클라우드 기반 보안',
                    desc: '영상 전송·저장 시 모두 암호화됩니다. 개인정보보호법과 의료법을 준수하여 운영합니다.',
                  },
                ].map(({ title, desc }) => (
                  <div key={title} className="b-item" style={{ display: 'flex', gap: 16, padding: '28px 24px' }}>
                    <div
                      style={{
                        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                        background: 'rgba(167,139,250,0.12)', color: '#a78bfa',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, fontWeight: 700,
                      }}
                    >
                      ✓
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>{title}</div>
                      <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 원칙6 외부 영상 정책 박스 */}
              <div
                style={{
                  marginTop: 32,
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 16,
                  padding: '28px 32px',
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginBottom: 16 }}>
                  외부 분석 결과 활용 정책
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { ok: true,  text: '외부 의료기관 분석 결과 수치를 차트에 기록하여 단순 비교할 수 있습니다.' },
                    { ok: true,  text: 'BeauBrain은 외부 영상을 재분석하지 않습니다.' },
                    { ok: true,  text: '분석 장비·트레이서·프로토콜이 다를 수 있으므로, 직접 비교에는 임상적 해석이 필요합니다.' },
                    { ok: false, text: '외부 PET 영상을 Amylo-Cloud로 재분석하는 것은 허가 사용 목적 범위 밖입니다.' },
                  ].map(({ ok, text }) => (
                    <div key={text} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>
                      <span style={{ color: ok ? '#34d399' : '#f87171', fontWeight: 700, flexShrink: 0 }}>{ok ? '✓' : '✗'}</span>
                      {text}
                    </div>
                  ))}
                </div>
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────── */}
        <section style={{ ...S, background: 'var(--bg-surface)' }}>
          <div className="container">
            <SectionBlock label="FAQ" title="자주 묻는 질문">
              <div style={{ maxWidth: 760, margin: '56px auto 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  {
                    q: '왜 Centiloid 단위를 사용하나요?',
                    a: 'Centiloid(CL)는 ADNI·AIBL 등 국제 합의를 기반으로 한 알츠하이머 영상 정량 평가의 글로벌 표준 단위입니다. SUVR 등 장비·트레이서별 다른 단위와 달리 장비·트레이서·기관이 달라도 단순 비교가 용이합니다.',
                  },
                  {
                    q: '의료기관에 PET 장비가 없는데 사용 가능한가요?',
                    a: '네. 환자가 외부 영상의학과에서 받은 PET 영상(CD 또는 PACS)을 업로드하여 분석할 수 있습니다. 영상 장비 없는 의료기관도 CL 정량 분석 결과를 환자에게 설명할 수 있습니다.',
                  },
                  {
                    q: '외부 의료기관 분석 결과와 비교할 수 있나요?',
                    a: '외부 기관의 분석 결과 수치를 환자 차트에 함께 기록하여 단순 수치 비교가 가능합니다. 단, BeauBrain은 외부 영상을 재분석하지 않습니다. 분석 장비·트레이서·프로토콜이 다를 수 있으므로 직접 비교에는 임상적 해석이 필요합니다.',
                  },
                  {
                    q: '분석 결과만으로 알츠하이머를 진단할 수 있나요?',
                    a: 'Amylo-Cloud는 판독보조 도구로, CL 수치를 제공합니다. 양성/음성 등 임상 판정과 치료 방향 결정은 담당의 종합 판단에 따릅니다.',
                  },
                ].map(({ q, a }) => (
                  <div key={q} className="b-item" style={{ padding: '24px 28px', borderRadius: 14 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 10, display: 'flex', gap: 12 }}>
                      <span className="num" style={{ color: 'var(--accent)', fontWeight: 800, flexShrink: 0 }}>Q.</span>
                      {q}
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.7, paddingLeft: 28 }}>{a}</div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          </div>
        </section>

      </SolutionLayout>
    </PublicLayout>
  );
}

export const Route = createFileRoute('/solutions/amylo-cloud')({
  component: Page,
});
